import React from 'react';

import { VisuallyHidden } from '@rbilabs/components-library/build/components/visually-hidden';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import IconCircleCheck from 'components/icons/circle-check';
import useFocusOnMountRef from 'hooks/use-focus-on-mount-ref';

import SelectionOptions from './selection-options';
import { SelectionOptionsTitle } from './selection-options-title';
import theme from './theme';

const SelectionOptionsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 1rem 0;

  svg {
    fill: ${theme.checkIconFillColor};
    width: 1.25rem;
    height: 1.25rem;
  }
`;

const SelectionOptionsRequired = styled.div`
  align-items: center;
  background-color: ${theme.selectionOptionsRequiredBackgroundColor};
  border-radius: ${Styles.borderRadius};
  color: ${Styles.color.white};
  display: flex;
  font-size: 0.75rem;
  height: 20px;
  justify-content: center;
  min-width: 68px;
  padding: 0 0.5rem;
`;

interface ISelectionContent {
  multiColumnLayout?: boolean;
}

const SelectionContent = styled.div<ISelectionContent>`
  margin-bottom: 2rem;
  display: flex;
  flex-direction: ${props => (props.multiColumnLayout ? 'row' : 'column')};
  flex-wrap: wrap;
`;

interface IProps {
  title?: string;
  focusOnMount?: boolean;
  required?: boolean;
  requirementSatisfied?: boolean;
  showCheckMarkOnRequirementSatisfied?: boolean;
  children: React.ReactNode;
  selection?: string;
  multiColumnLayout?: boolean;
}

export const SelectionContainer: React.FC = () => null;

const SelectionContainerInternal = ({
  title,
  focusOnMount,
  required,
  requirementSatisfied,
  showCheckMarkOnRequirementSatisfied = true,
  selection,
  multiColumnLayout,
  children,
}: IProps) => {
  const titleRef = useFocusOnMountRef(!!focusOnMount);
  const { formatMessage } = useIntl();

  return (
    <SelectionOptions data-testid={`selection-${title}`}>
      <SelectionOptionsHeader>
        <SelectionOptionsTitle ref={titleRef} tabIndex={-1}>
          {title}
        </SelectionOptionsTitle>
        {required &&
          (requirementSatisfied ? (
            showCheckMarkOnRequirementSatisfied && <IconCircleCheck isActive />
          ) : (
            <SelectionOptionsRequired>{formatMessage({ id: 'required' })}</SelectionOptionsRequired>
          ))}
      </SelectionOptionsHeader>
      <SelectionContent multiColumnLayout={!!multiColumnLayout}>{children}</SelectionContent>
      {selection && <VisuallyHidden role="alert">{selection}</VisuallyHidden>}
    </SelectionOptions>
  );
};

export default SelectionContainerInternal;
